import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../../firebase-config";
import "../../assets/styles/ClientShared.css";

const requestDefaults = {
  requestType: "Small website edit",
  priority: "Normal",
  pageOrFeature: "",
  details: "",
  deadline: "",
};

const coveredRequests = [
  "Text, image, phone number, hours, testimonial, or link updates",
  "Small layout tweaks to existing sections",
  "Bug reports for existing forms, buttons, or workflows",
  "Uptime, backup, or support questions",
];

const notCoveredRequests = [
  "New full pages, redesigns, or brand-new sections",
  "New custom features, portals, dashboards, booking systems, or e-commerce",
  "Large integrations, ad management, or SEO/content campaigns",
  "Work beyond the included monthly hour unless quoted separately",
];

const CarePlanRequest = () => {
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState("");
  const [projectData, setProjectData] = useState(null);
  const [request, setRequest] = useState(requestDefaults);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error("User not authenticated.");

        const userSnap = await getDoc(doc(db, "users", currentUser.uid));
        if (!userSnap.exists()) throw new Error("User document not found.");

        const userData = userSnap.data();
        if (!userData.projectId) throw new Error("Project ID not found.");

        const projectSnap = await getDoc(doc(db, "projects", userData.projectId));
        if (!projectSnap.exists()) throw new Error("Project document not found.");

        setProjectId(userData.projectId);
        setProjectData(projectSnap.data());
      } catch (error) {
        console.error("Error loading care request:", error);
        setMessageType("error");
        setMessage("Unable to load your project. Please contact support.");
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, []);

  const updateField = (field, value) => {
    setRequest(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!request.details.trim()) {
      setMessageType("error");
      setMessage("Please describe what you need changed or checked.");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...request,
        id: `care-${Date.now()}`,
        status: "new",
        submittedAt: new Date().toISOString(),
        messages: [
          {
            id: `msg-${Date.now()}`,
            author: "client",
            body: request.details,
            createdAt: new Date().toISOString(),
          },
        ],
      };
      const updatedRequests = [...(projectData?.carePlanRequests || []), payload];

      await updateDoc(doc(db, "projects", projectId), {
        carePlanRequests: updatedRequests,
        latestCarePlanRequest: payload,
      });

      setProjectData(prev => ({ ...prev, carePlanRequests: updatedRequests, latestCarePlanRequest: payload }));
      setRequest(requestDefaults);
      setMessageType("success");
      setMessage("Request submitted. Next Level Websites will review it and follow up.");
    } catch (error) {
      console.error("Error submitting care request:", error);
      setMessageType("error");
      setMessage("Unable to submit this request right now. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="cp-loading"><p>Loading care request...</p></div>;

  const activeCarePlan = projectData?.carePlanStatus === "active";
  const requests = projectData?.carePlanRequests || [];

  return (
    <div className="cp-page cp-page--narrow">
      <div className="cp-page-header">
        <p className="cp-eyebrow">Client Portal</p>
        <h1 className="cp-title">Care Plan Request</h1>
        <p className="cp-subtitle">Submit edits, fixes, monitoring questions, or small support requests for your website.</p>
      </div>

      {message && (
        <p className={messageType === "error" ? "cp-message cp-message--error" : "cp-message"} style={{ marginBottom: "20px" }}>
          {message}
        </p>
      )}

      <div className="cp-section" style={{ marginBottom: "20px" }}>
        <div className="cp-section-header">
          <h2 className="cp-section-title">Plan Status</h2>
          {activeCarePlan ? <span className="cp-badge cp-badge--active">Active</span> : <span className="cp-badge cp-badge--pending">Not Active</span>}
        </div>
        <div className="cp-section-body">
          <ul className="cp-list">
            <li className="cp-list-item"><strong>Project:</strong> {projectData?.projectName || "Website project"}</li>
            <li className="cp-list-item"><strong>Care Plan:</strong> {projectData?.carePlanType || "Not selected"}</li>
            <li className="cp-list-item"><strong>Monthly edit time:</strong> Up to 1 hour unless your agreement says otherwise.</li>
          </ul>
        </div>
      </div>

      <div className="cp-section" style={{ marginBottom: "20px" }}>
        <div className="cp-section-header">
          <h2 className="cp-section-title">Request History</h2>
          <span className="cp-badge cp-badge--pending">{requests.length} Total</span>
        </div>
        <div className="cp-section-body">
          {requests.length ? (
            <div className="cp-doc-list">
              {requests.slice().reverse().map((item, index) => (
                <div key={item.id || item.submittedAt || index} className="cp-doc-item" style={{ alignItems: "flex-start", gap: "16px" }}>
                  <div>
                    <p className="cp-doc-label" style={{ margin: "0 0 6px" }}>{item.requestType || "Care Plan Request"}</p>
                    <p style={{ fontFamily: "var(--font-ui)", fontSize: "0.875rem", color: "var(--text-tertiary)", margin: "0 0 8px" }}>
                      {item.pageOrFeature || "No page specified"} {item.deadline ? `· Preferred deadline: ${item.deadline}` : ""}
                    </p>
                    <p style={{ fontFamily: "var(--font-body)", fontSize: "0.9375rem", color: "var(--text-secondary)", lineHeight: 1.6, margin: "0 0 8px" }}>
                      {item.details}
                    </p>
                    <p style={{ fontFamily: "var(--font-ui)", fontSize: "0.875rem", color: "var(--text-secondary)", lineHeight: 1.6, margin: 0 }}>
                      <strong>Status:</strong> {item.status || "new"}
                    </p>
                  </div>
                  <span className={`cp-badge ${item.status === "done" ? "cp-badge--active" : "cp-badge--pending"}`}>
                    {item.status || "new"}
                  </span>
                  <button
                    className="cp-doc-link"
                    onClick={() => navigate(`/client/care-plan-request/${encodeURIComponent(item.id || item.submittedAt || index)}`)}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    Open &rarr;
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="cp-empty-text">No requests submitted yet.</p>
          )}
        </div>
      </div>

      <div className="cp-grid-2" style={{ marginBottom: "20px" }}>
        <div className="cp-section" style={{ margin: 0 }}>
          <div className="cp-section-header">
            <h2 className="cp-section-title">Covered Requests</h2>
          </div>
          <div className="cp-section-body">
            <ul className="cp-list">
              {coveredRequests.map(item => <li key={item} className="cp-list-item">{item}</li>)}
            </ul>
          </div>
        </div>
        <div className="cp-section" style={{ margin: 0 }}>
          <div className="cp-section-header">
            <h2 className="cp-section-title">Quoted Separately</h2>
          </div>
          <div className="cp-section-body">
            <ul className="cp-list">
              {notCoveredRequests.map(item => <li key={item} className="cp-list-item">{item}</li>)}
            </ul>
          </div>
        </div>
      </div>

      {!activeCarePlan && (
        <div className="cp-section" style={{ marginBottom: "20px" }}>
          <div className="cp-section-header">
            <h2 className="cp-section-title">Request Access Locked</h2>
          </div>
          <div className="cp-section-body">
            <p style={{ fontFamily: "var(--font-ui)", fontSize: "0.9375rem", color: "var(--text-secondary)", lineHeight: 1.7, margin: 0 }}>
              Care Plan requests open after your subscription is active. If you already paid, Next Level Websites will confirm the plan and unlock this page.
            </p>
            <div className="cp-btn-row">
              <button className="cp-btn-secondary" onClick={() => navigate("/client")}>Back to Dashboard</button>
            </div>
          </div>
        </div>
      )}

      {activeCarePlan && (
      <div className="cp-section">
        <div className="cp-section-header">
          <h2 className="cp-section-title">New Request</h2>
        </div>
        <div className="cp-section-body">
          <div className="cp-form-group">
            <label>Request Type</label>
            <select value={request.requestType} onChange={e => updateField("requestType", e.target.value)}>
              <option>Small website edit</option>
              <option>Bug or broken link</option>
              <option>Content update</option>
              <option>Performance or uptime concern</option>
              <option>Question or support</option>
            </select>
          </div>

          <div className="cp-form-group">
            <label>Priority</label>
            <select value={request.priority} onChange={e => updateField("priority", e.target.value)}>
              <option>Normal</option>
              <option>High</option>
              <option>Urgent</option>
            </select>
          </div>

          <div className="cp-form-group">
            <label>Page, URL, or Feature</label>
            <input value={request.pageOrFeature} onChange={e => updateField("pageOrFeature", e.target.value)} placeholder="Example: Home page hero, Services page, contact form" />
          </div>

          <div className="cp-form-group">
            <label>What do you need?</label>
            <textarea value={request.details} onChange={e => updateField("details", e.target.value)} placeholder="Describe the change, issue, or support request. Include exact wording if content needs to be replaced." />
          </div>

          <div className="cp-form-group">
            <label>Preferred Deadline</label>
            <input type="date" value={request.deadline} onChange={e => updateField("deadline", e.target.value)} />
          </div>

          <div className="cp-btn-row">
            <button className="cp-btn-primary" onClick={handleSubmit} disabled={saving}>{saving ? "Submitting..." : "Submit Request"}</button>
            <button className="cp-btn-secondary" onClick={() => navigate("/client")}>Back to Dashboard</button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default CarePlanRequest;
